import { __assign, __awaiter, __generator } from "tslib";
import { initialize } from '@ms/centro-hvc-loader';
import { FeedbackUiType } from '../types';
import { IN_APP_FEEDBACK_LOADER_VERSION } from './CompileTimeConstants';
import { CentroUrlProd, DefaultMajorVersion, guidRegexPattern, inappFeedbackLoaderVersionPrefix, PerfPrefix, } from './Constants';
import { logError, logInfo } from './Logging';
export var getCentroParams = function (initializationInfo) {
    var hostName = initializationInfo.hostName, locale = initializationInfo.locale, centroUrl = initializationInfo.centroUrl, specificHvcVersion = initializationInfo.specificHvcVersion, majorVersion = initializationInfo.majorVersion, centroHvcData = initializationInfo.centroHvcData, telemetryAppId = initializationInfo.telemetryAppId, useCentroConfig = initializationInfo.useCentroConfig;
    var initializationConfig = {
        hostName: hostName,
        locale: locale,
        appId: telemetryAppId,
        // All perf log will be prefixed with this string.
        // So that when we analyze perf logs, we know that those logs are coming from HVC running in your environment.
        perfPrefix: "".concat(PerfPrefix, "_").concat(hostName),
        useCentroConfig: useCentroConfig,
    };
    var centroParams = {
        centroEnvironmentBaseUrl: centroUrl !== null && centroUrl !== void 0 ? centroUrl : CentroUrlProd,
        preConfig: initializationConfig,
        // Note: do not specify both forHostVersion and hvcVersion
        forHostVersion: majorVersion !== null && majorVersion !== void 0 ? majorVersion : DefaultMajorVersion, // current stable major version of feedback
        hvcVersion: '', // to retrieve specific version of HVC
        centroHvcData: centroHvcData !== null && centroHvcData !== void 0 ? centroHvcData : '', // specific data used for debugging purposes
        enableUxVersionQueryParam: true, // get uxversion information with query params instead of request headers (recommended) & mandatory for ECS usage
    };
    if (specificHvcVersion) {
        centroParams.hvcVersion = specificHvcVersion;
        centroParams.forHostVersion = '';
        return centroParams;
    }
    return centroParams;
};
export function validateInitializationParameters(initializationInfo) {
    return __awaiter(this, void 0, void 0, function () {
        var inAppFeedbackRef, hostName, locale, telemetryAppId, missingParameterError, errorMessageList, parameterError;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    inAppFeedbackRef = initializationInfo.inAppFeedbackRef, hostName = initializationInfo.hostName, locale = initializationInfo.locale, telemetryAppId = initializationInfo.telemetryAppId;
                    missingParameterError = 'is a mandatory parameter';
                    errorMessageList = [];
                    if (!inAppFeedbackRef) {
                        errorMessageList.push("inAppFeedbackRef ".concat(missingParameterError));
                    }
                    if (!hostName) {
                        errorMessageList.push("hostName ".concat(missingParameterError));
                        initializationInfo.hostName = 'OcvFeedbackDemoHostingApp'; // setting a default value to make sure initialize is not failing
                    }
                    if (!locale) {
                        errorMessageList.push("locale ".concat(missingParameterError));
                        initializationInfo.locale = 'en'; // setting a default value to make sure initialize is not failing
                    }
                    if (telemetryAppId && !guidRegexPattern.test(telemetryAppId)) {
                        errorMessageList.push('telemetryAppId needs to be GUID');
                    }
                    if (!(errorMessageList.length > 0)) return [3 /*break*/, 2];
                    // This is to enable dynamic logging endpoint to be GDPR and EUDB compliant
                    return [4 /*yield*/, initialize(getCentroParams(initializationInfo))];
                case 1:
                    // This is to enable dynamic logging endpoint to be GDPR and EUDB compliant
                    _a.sent();
                    parameterError = new Error(JSON.stringify(errorMessageList));
                    logError("532379" /* LoggingTags.FeedbackInitDataValidationError */, 'FeedbackInitData Validation Error: ', parameterError);
                    if (initializationInfo.onInitializationError) {
                        initializationInfo.onInitializationError(parameterError);
                    }
                    throw parameterError;
                case 2: return [2 /*return*/];
            }
        });
    });
}
var buildInAppFeedbackLoaderVersionString = function (sdkVersion) {
    var inappFeedbackLoaderVersionString = "".concat(inappFeedbackLoaderVersionPrefix).concat(IN_APP_FEEDBACK_LOADER_VERSION);
    if (!sdkVersion) {
        return inappFeedbackLoaderVersionString;
    }
    // `setInitTelemetryData()` can sometimes be called multiple times and so this avoids duplicating the
    // `inappFeedbackLoaderVersionString` if it is already in the `sdkVersion`
    if (sdkVersion.includes(inappFeedbackLoaderVersionPrefix)) {
        return sdkVersion;
    }
    return "".concat(sdkVersion, " | ").concat(inappFeedbackLoaderVersionString);
};
/**
 * This function adds common data to the feedback init options
 * Currently adds hvcInvocationTimestamp
 * To be added: inapp-feedback loader version
 * @param iFeedbackInitOptions
 * @returns iFeedbackInitOptions
 */
export var setInitTelemetryData = function (iFeedbackInitOptions) {
    var _a, _b, _c, _d, _e, _f;
    var sdkVersion = buildInAppFeedbackLoaderVersionString((_a = iFeedbackInitOptions.telemetry) === null || _a === void 0 ? void 0 : _a.sdkVersion);
    if ((_b = iFeedbackInitOptions.feedbackConfig) === null || _b === void 0 ? void 0 : _b.isDisplayed) {
        iFeedbackInitOptions.performanceTimestamps = __assign(__assign({}, iFeedbackInitOptions.performanceTimestamps), { hvcInvocationTimestamp: Date.now() });
    }
    /**
     * Log the request to open the feedback form
     * The params in the validation are the primary params that are required to open the feedback form
     */
    if (((_c = iFeedbackInitOptions.feedbackConfig) === null || _c === void 0 ? void 0 : _c.isDisplayed) ||
        ((_d = iFeedbackInitOptions.feedbackConfig) === null || _d === void 0 ? void 0 : _d.feedbackUiType) === FeedbackUiType.NoSurface ||
        ((_e = iFeedbackInitOptions.feedbackConfig) === null || _e === void 0 ? void 0 : _e.feedbackUiType) === FeedbackUiType.NoSurfaceWithoutTitle) {
        logInfo("532390" /* LoggingTags.OpenFeedbackFormRequested */, 'Open Feedback Form Requested', {
            clientFeedbackId: (_f = iFeedbackInitOptions.clientFeedbackId) !== null && _f !== void 0 ? _f : null,
            sdkVersion: sdkVersion,
        });
    }
    if (iFeedbackInitOptions.telemetry) {
        iFeedbackInitOptions.telemetry.sdkVersion = sdkVersion;
    }
    else {
        iFeedbackInitOptions.telemetry = { sdkVersion: sdkVersion };
    }
    return iFeedbackInitOptions;
};
